import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  TouchableOpacity,
} from 'react-native';
import {
  RenderItemParams,
  NestableScrollContainer,
  NestableDraggableFlatList,
} from 'react-native-draggable-flatlist';
import { Ionicons } from '@expo/vector-icons';
import { databaseService } from '../../services/databaseService';
import { APP_CONSTANTS } from '../../config/constants';
import theme from '../../config/theme';
import { FieldOrderingLogger } from '../../utils/logger';
import { assertStructuralOnline } from '../../services/checklist/ChecklistStructuralOfflineGate';
import { checklistStructuralMutationService } from '../../services/checklist/ChecklistStructuralMutationService';

/*************** Tipagens *****************/
interface FieldItem {
  id: string;
  grupo: string;
  nome: string;
  ordem: number;
  retired_at?: string | null;
}

interface Props {
  // opcional para filtrar por template
  templateId?: string;
}

// Representação interna para o FlatList de grupos
interface GroupItem {
  id: string; // Nome do grupo (id único)
  title: string;
  fields: FieldItem[];
}

/**
 * FieldOrderingBoard (versão mobile / .native)
 * --------------------------------------------------
 * – Exibe lista de grupos e seus campos com suporte a drag & drop.
 * – Permite reordenar grupos e, dentro de cada grupo, reordenar campos.
 * – Persistência realizada em lote via mutador estrutural do checklist.
 *
 * ATENÇÃO: Depende da lib `react-native-draggable-flatlist` (gestos nativos).
 */
const FieldOrderingBoardNative: React.FC<Props> = ({ templateId }) => {
  // Estado bruto dos campos vindos do banco
  const [fields, setFields] = useState<FieldItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [blockedMessage, setBlockedMessage] = useState<string | null>(null);
  // Flag para evitar persistências simultâneas
  const savingRef = useRef(false);

  /* ---------------- Carregamento inicial ---------------- */
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const query: any = {};
        if (templateId) query.template_id = templateId;
        const items = await databaseService.listItems<FieldItem>(
          APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CAMPOS_TEMPLATE.NAME,
          query
        );
        const activeItems = items.filter(item => !item.retired_at);
        // Ordenar pela coluna ordem
        activeItems.sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));
        if (mounted) setFields(activeItems);
      } catch (error) {
        FieldOrderingLogger.Main.error('Erro ao carregar campos:', error);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [templateId]);

  /* --------------- Agrupamento de campos --------------- */
  const groups: GroupItem[] = useMemo(() => {
    const map = new Map<string, FieldItem[]>();
    fields.forEach((f) => {
      const grp = f.grupo || 'Sem grupo';
      if (!map.has(grp)) map.set(grp, []);
      map.get(grp)!.push(f);
    });

    // Ordenar internamente os campos
    map.forEach((arr) => arr.sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0)));

    // Ordenar grupos pela menor ordem interna
    const result = Array.from(map.entries())
      .sort((a, b) => {
        const firstA = a[1][0];
        const firstB = b[1][0];
        return (firstA.ordem ?? 0) - (firstB.ordem ?? 0);
      })
      .map<GroupItem>(([grpName, itens]) => ({
        id: grpName,
        title: grpName,
        fields: itens,
      }));

    return result;
  }, [fields]);

  /* ---------------- Persistência ---------------- */
  /**
   * Recalcula a coluna `ordem` sequencialmente e persiste no banco.
   * Recebe estrutura completa (grupos + campos) já no formato final.
   */
  const recomputeOrderAndPersist = async (updatedGroups: GroupItem[]) => {
    const gate = assertStructuralOnline('field', 'reorder');
    if (!gate.allowed) {
      setBlockedMessage(gate.message);
      return;
    }
    if (!templateId) {
      setBlockedMessage('Selecione um modelo de checklist antes de reordenar os campos.');
      return;
    }

    setBlockedMessage(null);
    if (savingRef.current) {
      FieldOrderingLogger.Main.warn('[SKIP] Persistência em andamento, ignorando');
      return;
    }
    savingRef.current = true;

    // Construir novo mapa {idCampo: novaOrdem}
    const orderMap: Record<string, number> = {};
    let current = 1;
    updatedGroups.forEach((group) => {
      group.fields.forEach((field) => {
        orderMap[field.id] = current++;
      });
    });
    FieldOrderingLogger.Main.info('[NOVA_ORDERMAP]', JSON.stringify(orderMap, null, 2));

    // Atualização otimista local
    setFields((prev) => prev.map((f) => ({ ...f, ordem: orderMap[f.id] })));

    /* --- Persistir somente itens que mudaram --- */
    const updates: Array<{ id: string; updates: Record<string, unknown> }> = [];
    updatedGroups.forEach((g: GroupItem) => {
      g.fields.forEach((it: FieldItem) => {
        const newOrder = orderMap[it.id];
        if (it.ordem !== newOrder) {
          updates.push({ id: it.id, updates: { ordem: newOrder } });
        }
      });
    });

    try {
      await checklistStructuralMutationService.reorderFields(templateId, updates);
      FieldOrderingLogger.Main.info(`[DB] ${updates.length} registros atualizados`);
    } catch (e) {
      FieldOrderingLogger.Main.error('Erro ao persistir ordens:', e);
    }

    /* --- Reload de garantia --- */
    try {
      const query: any = {};
      if (templateId) query.template_id = templateId;
      const fresh = await databaseService.listItems<FieldItem>(
        APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CAMPOS_TEMPLATE.NAME,
        query
      );
      const activeFresh = fresh.filter(item => !item.retired_at);
      activeFresh.sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));
      setFields(activeFresh);
    } catch (e) {
      FieldOrderingLogger.Main.error('Erro ao recarregar pós-persistência:', e);
    } finally {
      savingRef.current = false;
    }
  };

  // Referência para lista externa (deve vir antes de qualquer retorno condicional)
  const outerRef = useRef<any>(null);

  /* ---------------- Handlers de drag ---------------- */
  const onGroupDragEnd = ({ data }: { data: GroupItem[] }) => {
    if (savingRef.current) return;
    recomputeOrderAndPersist(data);
  };

  const onFieldDragEnd = (
    groupName: string,
    params: { data: FieldItem[] }
  ): void => {
    if (savingRef.current) return;

    const { data } = params;
    const newGroups = groups.map((g: GroupItem) =>
      g.id === groupName ? { ...g, fields: data } : g
    );

    recomputeOrderAndPersist(newGroups);
  };

  /* ---------------- Renders ---------------- */
  if (loading) {
    return (
      <View style={styles.loaderContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary.default} />
      </View>
    );
  }

  // Render de cada campo dentro do grupo
  const renderFieldItem = ({
    item,
    drag,
    isActive,
  }: RenderItemParams<FieldItem>) => (
    <View style={[styles.fieldRow, isActive && { opacity: 0.7 }]}>
      <TouchableOpacity onLongPress={drag} delayLongPress={150}>
        <Ionicons
          name="reorder-two"
          size={18}
          color={theme.colors.text.secondary}
          style={styles.fieldDragHandle}
        />
      </TouchableOpacity>
      <Text style={styles.fieldName}>
        {item.nome}{' '}
        <Text style={styles.ordemBadge}>#{item.ordem}</Text>
      </Text>
    </View>
  );

  // Render de cada grupo (header + lista de campos)
  const renderGroupItem = ({
    item,
    drag,
    isActive,
  }: RenderItemParams<GroupItem>) => (
    <View style={[styles.groupContainer, isActive && { opacity: 0.65 }]}>
      <View style={styles.groupHeader}>
        <TouchableOpacity
          onLongPress={drag}
          delayLongPress={180}
          hitSlop={6}
          activeOpacity={0.7}
          style={{ marginRight: theme.spacing.sm }}
        >
          <Ionicons
            name="reorder-three"
            size={20}
            color={theme.colors.text.secondary}
          />
        </TouchableOpacity>
        <Text style={styles.groupTitle}>{item.title || 'Sem grupo'}</Text>
      </View>

      {/* Lista de campos dentro do grupo */}
      <NestableDraggableFlatList
        data={item.fields}
        keyExtractor={(f: FieldItem) => f.id}
        renderItem={renderFieldItem}
        onDragEnd={(params: { data: FieldItem[] }) => onFieldDragEnd(item.id, params)}
        nestedScrollEnabled
        //activationDistance={4}
        simultaneousHandlers={outerRef}
        contentContainerStyle={{ backgroundColor: theme.colors.background.primary }}
      />
    </View>
  );

  return (
    <NestableScrollContainer>
      {blockedMessage ? (
        <View style={styles.blockedBanner}>
          <Ionicons name="cloud-offline-outline" size={18} color={theme.colors.util.error} />
          <Text style={styles.blockedBannerText}>{blockedMessage}</Text>
        </View>
      ) : null}
      <NestableDraggableFlatList
        ref={outerRef}
        data={groups}
        keyExtractor={(g: GroupItem) => g.id}
        renderItem={renderGroupItem}
        onDragEnd={onGroupDragEnd}
        //activationDistance={4}
        contentContainerStyle={{ paddingBottom: theme.spacing.lg, marginTop: theme.spacing.md }}
      />
    </NestableScrollContainer>
  );
};

/***************** Estilos *****************/
const styles = StyleSheet.create({
  loaderContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background.secondary,
  },
  blockedBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginHorizontal: theme.spacing.md,
    marginTop: theme.spacing.md,
    marginBottom: theme.spacing.sm,
    padding: theme.spacing.sm,
    backgroundColor: theme.colors.background.primary,
    borderLeftWidth: 3,
    borderLeftColor: theme.colors.util.error,
  },
  blockedBannerText: {
    flex: 1,
    marginLeft: theme.spacing.sm,
    color: theme.colors.text.secondary,
    fontSize: theme.fontSizes.sm,
  },
  groupContainer: {
    backgroundColor: theme.colors.background.primary,
    marginHorizontal: theme.spacing.md,
    marginBottom: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    ...theme.shadows.sm,
  },
  groupHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    backgroundColor:
      theme.colors.background.tertiary || theme.colors.background.secondary,
    borderTopLeftRadius: theme.borderRadius.md,
    borderTopRightRadius: theme.borderRadius.md,
  },
  groupTitle: {
    fontSize: theme.fontSizes.lg,
    fontWeight: theme.fontWeights.semibold as any,
    color: theme.colors.text.primary,
  },
  fieldRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border.light,
    backgroundColor: theme.colors.background.primary,
  },
  fieldDragHandle: {
    marginRight: theme.spacing.sm,
  },
  fieldName: {
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.secondary,
  },
  ordemBadge: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.util.info,
    marginLeft: 4,
  },
});

export default FieldOrderingBoardNative;
